import { useCallback, useEffect, useMemo, useRef } from 'react';
import { FormProvider, useFieldArray, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Paper,
  styled,
  Typography,
  useTheme,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';

import { TripLegType } from '@/data/models/reservation.model';
import { ServicePlan } from '@/data/models/service-plan.model';
import { selectPassengerClientData } from '@/data/client.slice';
import { emptyTripSegment } from './schema/reservationForm.schema';

import { useAppSelector } from '@/hooks/useAppSelector';
import { useDebugReservationMachines } from './useDebugReservationMachines';

import {
  canSaveAsDraftFromContext,
  getAggregatedFormValues,
} from './machines/reservationFormMain.machine';
import {
  createReservationFormSchema,
  emptyReservationFormValues,
  type ReservationFormInput,
  type ReservationFormValues,
} from './schema/reservationForm.schema';
import { useReservationFormMachines } from './ReservationFormMachinesContext';
import { useReservationPortal } from './ReservationPortalContext';

import { MultiTripLegsSection } from './fields/MultiTripLegsSection';
import { TripLegFieldsRow } from './fields/TripLegFieldsRow';
import { TripMachineCountSync } from './fields/TripMachineCountSync';
import { TripTypeField } from './fields/TripTypeField';
import { StandingOrderForm } from './fields/StandingOrderForm';
import PassengerBenefits from '../home/passengers/PassengerBenefits';

const PREFIX = 'ReservationTripLegForm';

const classes = {
  container: `${PREFIX}-container`,
  title: `${PREFIX}-title`,
  form: `${PREFIX}-form`,
  benefits: `${PREFIX}-benefits`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    marginBottom: theme.spacing(2),
    paddingTop: 0,
    paddingBottom: theme.spacing(2),
    flexGrow: 1,
    display: 'flex',
    justifyContent: 'space-around',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
  [`& .${classes.title}`]: {
    fontFamily: 'Bitter',
    fontWeight: 700,
    fontSize: 21,
    marginBottom: theme.spacing(1),
  },
  [`& .${classes.form}`]: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
    gap: theme.spacing(2),
    flex: 1,
    width: '-webkit-fill-available',
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(4),
    },
  },
  [`& .${classes.benefits}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    minWidth: 550,
  },
}));

interface ReservationTripLegFormProps {
  form: UseFormReturn<ReservationFormInput, unknown, ReservationFormValues>;
  onSubmit?: (values: ReservationFormValues) => Promise<void>;
  handleSaveAsDraft: () => void;
  handleOverrideOpen: () => void;
  servicePlans: ServicePlan[];
}

function ReservationTripLegForm({
  form,
  onSubmit,
  handleSaveAsDraft,
  handleOverrideOpen,
  servicePlans,
}: ReservationTripLegFormProps) {
  const isPortal = useReservationPortal();
  const { t } = useTranslation('common');
  const reservationFormSchema = useMemo(
    () => createReservationFormSchema(null, t),
    [t],
  );
  const theme = useTheme();
  // Uncomment the following line to see all state machine debug output in the console
  useDebugReservationMachines();

  const passengerClientData = useAppSelector(selectPassengerClientData);

  const { mainActor, mainSnapshot } = useReservationFormMachines();
  

  const { append, remove, fields } = useFieldArray({
    control: form.control,
    name: 'trips',
  });

  const tripType = form.watch('tripType');
  const watchedTrips = form.watch('trips');

  const handleValid = useCallback(() => {
    mainActor.send({ type: 'SUBMIT' });
  }, [mainActor]);

  const wasInSubmittingRef = useRef(false);
  useEffect(() => {
    const submitting = mainSnapshot.matches('submitting');
    if (!submitting) {
      wasInSubmittingRef.current = false;
      return;
    }
    if (wasInSubmittingRef.current) return;
    wasInSubmittingRef.current = true;

    const machineValues = getAggregatedFormValues(mainSnapshot.context);
    const formTrips = form.getValues('trips');
    const values = {
      ...machineValues,
      trips: machineValues.trips.map((mt, i) => ({
        ...mt,
        ...(formTrips[i] ?? {}),
      })),
    } as unknown as ReservationFormValues;
    const parsed = reservationFormSchema.safeParse(values);
    if (!parsed.success) {
      mainActor.send({ type: 'SUBMIT_ERROR' });
      return;
    }
    if (!onSubmit) {
      mainActor.send({ type: 'SUBMIT_SUCCESS' });
      return;
    }
    onSubmit(parsed.data)
      .then(() => mainActor.send({ type: 'SUBMIT_SUCCESS' }))
      .catch(() => mainActor.send({ type: 'SUBMIT_ERROR' }));
  }, [mainSnapshot, mainActor, onSubmit, form, reservationFormSchema]);

  const isSubmitting = mainSnapshot.matches('submitting');
  const isSuccess = mainSnapshot.matches('success');

  const aggregatedValues = getAggregatedFormValues(mainSnapshot.context);
  const mergedValues = {
    ...aggregatedValues,
    trips: aggregatedValues.trips.map((mt, i) => ({
      ...mt,
      ...(watchedTrips?.[i] ?? {}),
    })),
  } as unknown as ReservationFormValues;
  // console.log('mergedValues (for next button)', mergedValues);
  // console.log(
  //   'next button zod result',
  //   reservationFormSchema.safeParse(mergedValues),
  // );

  const canSaveAsDraft = useMemo(
    () => canSaveAsDraftFromContext(mainSnapshot.context),
    [mainSnapshot],
  );

  const handleReset = useCallback(() => {
    form.reset(emptyReservationFormValues);
    mainActor.send({ type: 'RESET_ALL' });
  }, [form, mainActor]);

  const handleAddTripLeg = () => {
    append(emptyTripSegment);
  };

  if (isSuccess) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="success.main" gutterBottom>
          Reservation submitted successfully.
        </Typography>
        <Button variant="outlined" onClick={handleReset}>
          Create another
        </Button>
      </Box>
    );
  }

  return (
    <FormProvider {...form}>
      <LocalizationProvider dateAdapter={AdapterLuxon}>
        <TripMachineCountSync />
        <StyledBox
          className={classes.container}
          component="form"
          onSubmit={form.handleSubmit(handleValid)}>
          <Paper elevation={2} className={classes.form}>
            <Typography className={classes.title} variant="h2">
              {t('member.tripDetails')}
            </Typography>
            <TripTypeField />
            {tripType === TripLegType.MultiLeg ? (
              <MultiTripLegsSection fields={fields} remove={remove} />
            ) : (
              <TripLegFieldsRow
                index={0}
                showRoundTripReturnFields={tripType === TripLegType.RoundTrip}
              />
            )}
            <StandingOrderForm index={0} />
            <Box sx={{ display: 'flex', gap: theme.spacing(2) }}>
              {tripType === TripLegType.MultiLeg ? (
                <Button
                  color="primary"
                  variant="contained"
                  onClick={handleAddTripLeg}>
                  {t('reservation.addLeg')}
                </Button>
              ) : null }
              <Button
                type="submit"
                variant="contained"
                disabled={
                  isSubmitting ||
                  !reservationFormSchema.safeParse(mergedValues).success
                }>
                {t('reservation.next')}
              </Button>
              {!isPortal && (
                <>
                  <Button
                    color="secondary"
                    variant="contained"
                    disabled={isSubmitting || !canSaveAsDraft}
                    onClick={handleSaveAsDraft}>
                    {t('reservation.saveAsDraft')}
                  </Button>
                  <Button
                    color="secondary"
                    variant="contained"
                    disabled={isSubmitting}
                    onClick={handleOverrideOpen}>
                    {t('reservation.requestOverride')}
                  </Button>
                </>
              )}
            </Box>
          </Paper>
          <Box className={classes.benefits}>
            <PassengerBenefits
              appointmentDateTime={
                form.getValues('trips.0.appointmentDateTime') ?? undefined
              }
              servicePlans={passengerClientData?.servicePlans || []}
            />
          </Box>
        </StyledBox>
      </LocalizationProvider>
    </FormProvider>
  );
}

export {
  type ReservationTripLegFormProps,
  ReservationTripLegForm,
}
